import React, { type JSX } from "react";
import { ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";

/** Speech bubble hint — visible only before a feature is selected. */
export function SelectFeatureHint(): JSX.Element {
  return (
    <div
      className="absolute pointer-events-none z-10 animate-in fade-in slide-in-from-bottom-2 duration-200 hidden sm:block"
      style={{ bottom: "100%", left: "0" }}
      role="status"
      aria-live="polite"
    >
      {/* Bubble box — SVG tail is absolutely positioned below this box */}
      <div className="inline-block bg-white rounded-xl px-4 py-2.5 font-cvcBody border-2 border-cvc-blue max-w-[calc(100vw-2rem)]">
        <span className="text-sm font-semibold text-cvc-blue">Select a feature above</span>
        {/* Downward-pointing SVG tail — same pattern as Tooltip component */}
        <svg
          width="20"
          height="10"
          viewBox="0 0 20 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute"
          style={{ bottom: "-10px", left: "16px" }}
          aria-hidden="true"
          focusable="false"
        >
          {/* Outer border path */}
          <path d="M 0 0 L 10 10 L 20 0 Z" fill="white" stroke="#0C4F88" strokeWidth="2" />
          {/* Inner white fill to cover the stroke on the top (flat) edge */}
          <path d="M 2 0 L 10 8 L 18 0 Z" fill="white" />
        </svg>
      </div>
    </div>
  );
}

/** Upload affordance; shows a spinner while a project is settling or uploading. */
export function UploadButton({
  disabled,
  busy,
  projectReady,
  title,
  onClick,
}: {
  disabled: boolean;
  busy: boolean;
  projectReady: boolean;
  title?: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="bg-white border border-cvc-grey text-cvc-text hover:text-cvc-blue hover:border-cvc-blue disabled:text-cvc-grey disabled:border-cvc-grey disabled:cursor-not-allowed p-3 sm:p-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-cvc-blue focus:ring-opacity-50 transition-colors duration-200 flex items-center justify-center min-h-[48px] min-w-[48px] sm:min-h-[52px] sm:min-w-[52px]"
      aria-label="Upload file"
      title={title}
    >
      {busy ? (
        <svg
          className={`animate-spin h-5 w-5 ${projectReady ? "text-cvc-blue" : "text-cvc-grey"}`}
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      ) : (
        <ArrowUpTrayIcon className="h-5 w-5 sm:h-6 sm:w-6" />
      )}
    </button>
  );
}

/** File type error popover. */
export function FileTypeErrorPopover({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss: () => void;
}): JSX.Element {
  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 mx-3 sm:mx-4 animate-in fade-in slide-in-from-bottom-2 duration-200">
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 shadow-lg flex items-start gap-2">
        <div className="flex-1">
          <p className="text-sm text-red-800 font-medium">Invalid file format</p>
          <p className="text-xs text-red-600 mt-1">{message}</p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-red-400 hover:text-red-600 p-1"
          aria-label="Dismiss error"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** Disclaimer plus the transient upload status / error surfaces. */
export function ChatInputFooter({
  uploadStatus,
  uploadError,
  fileTypeError,
  onDismissFileTypeError,
}: {
  uploadStatus: string;
  uploadError: string;
  fileTypeError: string;
  onDismissFileTypeError: () => void;
}): JSX.Element {
  return (
    <>
      {/* AI disclaimer */}
      <p className="text-xs text-center text-gray-400 mt-2">
        DealOS can make mistakes. Always check source documents.
      </p>

      {(uploadStatus || uploadError) && (
        <div className="mt-2">
          {uploadStatus && <p className="text-xs text-cvc-green">{uploadStatus}</p>}
          {uploadError && <p className="text-xs text-cvc-coral">{uploadError}</p>}
        </div>
      )}

      {fileTypeError && (
        <FileTypeErrorPopover message={fileTypeError} onDismiss={onDismissFileTypeError} />
      )}
    </>
  );
}
